/**
 * Gap-fill (frontend auth UI), judgement call 6 — the `?redirect=` query
 * parameter on `/sign-in` is attacker-influenced: a crafted link can set it
 * to anything, and it must never become an open redirect. A real, directly
 * testable pure function, not inlined ad hoc in `SignIn.tsx`.
 *
 * The rule: the value must start with exactly one `/` and must not start
 * with `//` (which every browser treats as protocol-relative — i.e.
 * off-site, `//evil.example` navigates to `https://evil.example`) or with
 * `/\` (a handful of browsers historically normalise a leading backslash to
 * a second forward slash, the same off-site outcome through a different
 * spelling). Anything else — an absolute URL (`https://evil.example`), a
 * value with no leading slash at all — is rejected. The caller's job is to
 * fall back to `/` when this returns `false`.
 */
export function isSafeRedirectTarget(value: string | null): boolean {
  if (!value) {
    return false;
  }
  if (!value.startsWith("/")) {
    return false;
  }
  if (value.startsWith("//") || value.startsWith("/\\")) {
    return false;
  }
  return true;
}

/** The value to actually navigate to: `value` itself if safe, `/` otherwise. */
export function safeRedirectTarget(value: string | null): string {
  return isSafeRedirectTarget(value) ? (value as string) : "/";
}
